import {mapRunningTimeDto} from '@domain/shared'
import {useStoppageCreate, stoppageDefaultObject} from '@domain/shifthandover'
import {ApiClient, PermissionType} from '@hconnect/apiclient'
import {DataContentWrapper} from '@hconnect/common/containers'
import {StoppageCreate as StoppageCreateDto} from '@hconnect/common/stoppages/types/stoppage.types'
import {
  AttachmentFile,
  RunningTime,
  RunningTimesEquipmentData,
  DowntimeStoppageCodes,
  StoppageDto
} from '@hconnect/common/types'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Box, Dialog, DialogContent, Typography, Theme, useMediaQuery} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import {
  StoppageFormContainer,
  ConfirmCloseDialog,
  EventAttachmentUpload
} from '@widgets/common-shift-event'
import {AxiosError} from 'axios'
import {AnimatePresence, motion} from 'framer-motion'
import React, {useMemo, useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {UndefinedStoppagesStepper, UndefinedStoppagesPrevNextButtons} from '../components'
import {RUNNING_TIMES_WIDGET_NAMESPACE} from '../consts'
import {useSwipeNavigation} from '../hooks'
import {getPrevItem, getNextItem, getUndefinedStoppages, getInitialStoppageData} from '../utils'

type Props = {
  onClose: () => void
  children: React.ReactNode
  item?: Partial<StoppageDto>
  plantId: string
  setItem: (item: Partial<StoppageDto>) => void
  equipment: RunningTimesEquipmentData
  currentUndefinedRunningTimes: RunningTime[] | undefined
  currentIndex: number | undefined
  onStoppageCreationSuccess: () => void
  stoppagesCount: number
  stoppageCodes: DowntimeStoppageCodes
  hasPermission: (permission: PermissionType) => boolean
  plantTimeZone: string
  isLoading: boolean
}

export const UndefinedStoppagesDialog: React.FC<Props> = ({
  setItem,
  isLoading,
  equipment,
  currentUndefinedRunningTimes,
  currentIndex,
  onStoppageCreationSuccess,
  onClose,
  children,
  stoppagesCount,
  item,
  plantId,
  stoppageCodes,
  hasPermission,
  plantTimeZone
}) => {
  const {t} = useTranslation(RUNNING_TIMES_WIDGET_NAMESPACE)
  const queryClient = useQueryClient()
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)
  const [files, setFiles] = useState<AttachmentFile[]>([])
  const formItem = useMemo(() => stoppageDefaultObject(item), [item])

  const handleStoppageCreationSuccess = useCallback(() => {
    spawnSuccessToast(t('stoppage.createMessage.success'))
    onStoppageCreationSuccess?.()
  }, [t, onStoppageCreationSuccess])

  const handleStoppageCreationError = useCallback(
    (error: AxiosError) => {
      const errorMessage =
        error.response?.status === 409
          ? t('stoppage.createMessage.errorConflict')
          : t('stoppage.createMessage.error')
      spawnErrorToast(errorMessage)
    },
    [t]
  )

  const createAction = useStoppageCreate(plantId, {
    onSuccess: handleStoppageCreationSuccess,
    onError: handleStoppageCreationError
  })

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const enableMainEquipmentPrefilling = useCommonFeatureFlag(FeatureFlagKey.MainEquipmentPrefill, {
    plantId
  })

  const handleClose = () => setConfirmCloseOpen(true)
  const handleCancelClose = () => setConfirmCloseOpen(false)
  const handleConfirmClose = () => {
    setConfirmCloseOpen(false)
    if (onClose) onClose()
  }

  const handleCreationSuccess = useCallback(async () => {
    const undefinedStoppages = getUndefinedStoppages(currentUndefinedRunningTimes)
    const nextStoppages = undefinedStoppages.filter(
      (rt) => !rt.beginTechnical?.isSame(item?.stoppageStart)
    )
    if (nextStoppages.length > 0) {
      const defaultValue = await getInitialStoppageData({
        rt: mapRunningTimeDto(nextStoppages[0]),
        equipment,
        plantId,
        queryClient,
        axiosInstance: ApiClient.axiosInstance,
        enableMainEquipmentPrefilling
      })
      setItem(defaultValue)
    }

    if (onStoppageCreationSuccess) {
      onStoppageCreationSuccess()
    }
  }, [
    currentUndefinedRunningTimes,
    onStoppageCreationSuccess,
    item?.stoppageStart,
    equipment,
    plantId,
    queryClient,
    enableMainEquipmentPrefilling,
    setItem
  ])

  const onSubmit = useCallback(
    async (data: StoppageCreateDto) => {
      await createAction.mutateAsync(data)
      const next = getNextItem(currentUndefinedRunningTimes, currentIndex)
      if (next) {
        await handleCreationSuccess()
      } else {
        onClose()
      }
    },
    [createAction, currentUndefinedRunningTimes, handleCreationSuccess, currentIndex, onClose]
  )

  const handlePrev = useCallback(
    async (fromIndex?: number) => {
      const index = fromIndex ?? currentIndex
      if (!currentUndefinedRunningTimes || index === undefined) return
      const prev = getPrevItem(currentUndefinedRunningTimes, index)
      if (prev) {
        const defaultValue = await getInitialStoppageData({
          rt: mapRunningTimeDto(prev),
          equipment,
          plantId,
          queryClient,
          axiosInstance: ApiClient.axiosInstance,
          enableMainEquipmentPrefilling
        })
        setItem(defaultValue)
      }
    },
    [
      currentIndex,
      currentUndefinedRunningTimes,
      equipment,
      plantId,
      queryClient,
      enableMainEquipmentPrefilling,
      setItem
    ]
  )

  const handleNext = useCallback(
    async (fromIndex?: number) => {
      const index = fromIndex ?? currentIndex
      if (!currentUndefinedRunningTimes || index === undefined) return
      const next = getNextItem(currentUndefinedRunningTimes, index)
      if (next) {
        const defaultValue = await getInitialStoppageData({
          rt: mapRunningTimeDto(next),
          equipment,
          plantId,
          queryClient,
          axiosInstance: ApiClient.axiosInstance,
          enableMainEquipmentPrefilling
        })
        setItem(defaultValue)
      }
    },
    [
      currentIndex,
      currentUndefinedRunningTimes,
      equipment,
      plantId,
      queryClient,
      enableMainEquipmentPrefilling,
      setItem
    ]
  )

  const handlePrevClick = useCallback(() => void handlePrev(), [handlePrev])
  const handleNextClick = useCallback(() => void handleNext(), [handleNext])

  const {activeStep, swipeDirectionRef, swipeContainerRef} = useSwipeNavigation({
    stoppagesCount,
    isMobile,
    onNext: handleNext,
    onPrev: handlePrev
  })

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        fullWidth
        maxWidth={isMobile ? false : 'lg'}
        PaperProps={{
          sx: {
            width: isMobile ? '98%' : '55%',
            maxWidth: 'none',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            ...(isMobile && {mx: 0})
          }
        }}
        data-test-id="undefinedStoppages-fillin-popup"
      >
        <DialogContent
          sx={{
            py: 0,
            px: isMobile ? 2 : 3,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {children}
          {isMobile ? (
            <Box
              ref={swipeContainerRef}
              sx={{overflowX: 'hidden', overflowY: 'auto', borderRadius: 2, position: 'relative'}}
            >
              <AnimatePresence initial={false} mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{opacity: 0, x: swipeDirectionRef.current * 60}}
                  animate={{opacity: 1, x: 0}}
                  exit={{opacity: 0, x: swipeDirectionRef.current * -60}}
                  transition={{duration: 0.2}}
                >
                  {currentUndefinedRunningTimes?.[activeStep] && (
                    <Box
                      key={currentUndefinedRunningTimes[activeStep].id}
                      sx={{backgroundColor: 'background.paper', borderRadius: 2}}
                    >
                      <DataContentWrapper
                        showLoader={isLoading || createAction.isPending}
                        isError={false}
                        isNoData={false}
                        placeholderWrapperSx={{minHeight: 'calc(100vh - 166px)'}}
                        progressSx={{color: 'primary.main'}}
                      >
                        <StoppageFormContainer
                          renderFormHeaderActions={() => (
                            <Typography data-test-id="stoppage-index">
                              {currentIndex !== undefined ? currentIndex + 1 : 1}/{stoppagesCount}
                            </Typography>
                          )}
                          data-test-id="stoppage-form-container"
                          doClose={onClose}
                          item={formItem}
                          plantId={plantId}
                          hiddenFields={{
                            hasSalesImpact: true,
                            stoppageType: true
                          }}
                          isAutocompleteProcessStage={true}
                          doSubmit={onSubmit}
                          timezone={plantTimeZone}
                          stoppageCodes={stoppageCodes}
                          disabledFields={{
                            stoppageStart: true,
                            stoppageEnd: true
                          }}
                        >
                          <EventAttachmentUpload
                            setAttachments={setFiles}
                            attachments={files}
                            hasPermission={hasPermission}
                          />
                        </StoppageFormContainer>
                      </DataContentWrapper>
                    </Box>
                  )}
                </motion.div>
              </AnimatePresence>
            </Box>
          ) : (
            <Box
              sx={{
                overflowY: 'auto',
                backgroundColor: 'background.paper',
                borderRadius: 1
              }}
            >
              <DataContentWrapper
                showLoader={isLoading || createAction.isPending}
                isError={false}
                isNoData={false}
                placeholderWrapperSx={{minHeight: 'calc(100vh - 166px)'}}
                progressSx={{color: 'primary.main'}}
              >
                <StoppageFormContainer
                  renderFormHeaderActions={() => (
                    <Typography data-test-id="stoppage-index">
                      {currentIndex !== undefined ? currentIndex + 1 : 1}/{stoppagesCount}
                    </Typography>
                  )}
                  doClose={onClose}
                  item={formItem}
                  plantId={plantId}
                  hiddenFields={{
                    hasSalesImpact: true,
                    stoppageType: true
                  }}
                  isAutocompleteProcessStage={true}
                  doSubmit={onSubmit}
                  timezone={plantTimeZone}
                  stoppageCodes={stoppageCodes}
                  disabledFields={{
                    stoppageStart: true,
                    stoppageEnd: true
                  }}
                >
                  <EventAttachmentUpload
                    setAttachments={setFiles}
                    attachments={files}
                    hasPermission={hasPermission}
                  />
                </StoppageFormContainer>
              </DataContentWrapper>
            </Box>
          )}
          {stoppagesCount > 1 && (
            <UndefinedStoppagesPrevNextButtons
              onPrev={handlePrevClick}
              onNext={handleNextClick}
              showPrev={currentIndex !== 0 && !isMobile}
              showNext={
                currentIndex !== undefined && currentIndex < stoppagesCount - 1 && !isMobile
              }
            />
          )}
          {isMobile && (
            <UndefinedStoppagesStepper activeStep={activeStep} stoppagesCount={stoppagesCount} />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmCloseDialog
        open={confirmCloseOpen}
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  )
}
